import React, { useState, useEffect } from 'react';
import api from '@/config/axios';
import { useAuth } from '../contexts/AuthContext';
import { Receipt, Plus, Edit, Trash2, Filter } from 'lucide-react';
import { useToastContext } from '../ui/feedback/ToastProvider';
import { useConfirmContext } from '../ui/feedback/ConfirmProvider';
import { FormDatePicker, KPICard, Card, Spinner, Button, Modal, DataTable } from '../ui';
import DirhamSymbol from '../components/DirhamSymbol';

const Expenses = () => {
  const { user: currentUser } = useAuth();
  const { error: showError, success: showSuccess } = useToastContext();
  const { confirm } = useConfirmContext();
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [checkedExpenses, setCheckedExpenses] = useState(new Set());
  const [editingExpense, setEditingExpense] = useState(null);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: ''
  });
  const [formData, setFormData] = useState({
    amount: 0,
    description: '',
    date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchExpenses();
  }, [filters]);

  const fetchExpenses = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;

      const response = await api.get('/api/expenses', { params });
      setExpenses(response.data.expenses);
    } catch (error) {
      console.error('Error fetching expenses:', error);
      showError('Failed to fetch expenses');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (expense = null) => {
    if (expense) {
      setEditingExpense(expense);
      const expenseDate = expense.date ? new Date(expense.date) : new Date();
      setFormData({
        amount: expense.amount || expense.amt || 0,
        description: expense.description || '',
        date: expenseDate
      });
    } else {
      setEditingExpense(null);
      setFormData({
        amount: 0,
        description: '',
        date: new Date()
      });
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        amount: formData.amount || 0,
        description: formData.description || '',
        date: formData.date instanceof Date 
          ? formData.date.toISOString() 
          : formData.date || new Date().toISOString()
      };

      if (editingExpense) {
        await api.put(`/api/expenses/${editingExpense._id}`, submitData);
        showSuccess('Expense updated successfully');
      } else {
        await api.post('/api/expenses', submitData);
        showSuccess('Expense created successfully');
      }
      fetchExpenses();
      setShowModal(false);
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to save expense');
    }
  };

  const handleDelete = async (expenseId) => {
    const confirmed = await confirm({
      title: 'Delete Expense',
      message: 'Are you sure you want to delete this expense? This action cannot be undone.',
      variant: 'danger',
      confirmText: 'Delete',
    });

    if (!confirmed) return;

    try {
      await api.delete(`/api/expenses/${expenseId}`);
      showSuccess('Expense deleted successfully');
      fetchExpenses();
    } catch (error) {
      showError('Failed to delete expense');
    }
  };

  const handleBulkDelete = async () => {
    if (checkedExpenses.size === 0) {
      showError('Please select at least one expense to delete');
      return;
    }

    const confirmed = await confirm({
      title: 'Delete Expenses',
      message: `Are you sure you want to delete ${checkedExpenses.size} expense(${checkedExpenses.size !== 1 ? 'es' : ''})? This action cannot be undone.`,
      variant: 'danger',
      confirmText: 'Delete All',
    });

    if (!confirmed) return;

    try {
      await Promise.all(
        Array.from(checkedExpenses).map(expenseId =>
          api.delete(`/api/expenses/${expenseId}`)
        )
      );
      showSuccess(`Deleted ${checkedExpenses.size} expense(${checkedExpenses.size !== 1 ? 'es' : ''})`);
      setCheckedExpenses(new Set());
      fetchExpenses();
    } catch (error) {
      showError('Failed to delete some expenses');
    }
  };

  const toggleExpenseCheck = (expenseId) => {
    const newChecked = new Set(checkedExpenses);
    if (newChecked.has(expenseId)) {
      newChecked.delete(expenseId);
    } else {
      newChecked.add(expenseId);
    }
    setCheckedExpenses(newChecked);
  };

  const toggleAllExpensesCheck = (checked) => {
    if (checked) {
      setCheckedExpenses(new Set(expenses.map(e => e._id)));
    } else {
      setCheckedExpenses(new Set());
    }
  };

  const totalExpenses = expenses.reduce((sum, exp) => sum + (exp.amount || exp.amt || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div className="flex items-center gap-3 sm:gap-4">
          <Receipt className="text-primary-600 dark:text-primary-400" size={28} />
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-primary-900 dark:text-primary-50">
              Expenses
            </h1>
            <p className="text-primary-600 dark:text-primary-400 mt-1.5 text-sm sm:text-base">
              Track business expenses
            </p>
          </div>
        </div>
        {currentUser?.role === 'admin' && (
          <Button onClick={() => handleOpenModal()} leftIcon={<Plus size={20} />}>
            Add Expense
          </Button>
        )}
      </div>

      {/* Summary Card */}
      <KPICard
        title="Total Expenses"
        value={
          <span className="flex items-center gap-1">
            <DirhamSymbol size={20} inline />
            {totalExpenses.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        }
        icon={<Receipt size={24} />}
        color="secondary"
      />

      {/* Filters */}
      <Card className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <Filter size={20} className="text-primary-600 dark:text-primary-400" />
          <h3 className="font-semibold text-primary-900 dark:text-primary-50">Filters</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormDatePicker
            label="Start Date"
            name="startDate"
            value={filters.startDate}
            onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
            placeholderText="Select start date"
          />
          <FormDatePicker
            label="End Date"
            name="endDate"
            value={filters.endDate}
            onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
            placeholderText="Select end date"
          />
        </div>
      </Card>

      <Card className="p-6">
        {checkedExpenses.size > 0 && (
          <div className="mb-4 p-4 bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800 rounded-lg flex items-center justify-between">
            <span className="text-error-700 dark:text-error-400 font-medium">
              {checkedExpenses.size} expense(s) selected
            </span>
            <Button
              onClick={handleBulkDelete}
              variant="ghost"
              size="sm"
              className="text-error-600 dark:text-error-400 hover:bg-error-100 dark:hover:bg-error-900/30"
              leftIcon={<Trash2 size={18} />}
            >
              Delete Selected
            </Button>
          </div>
        )}
        {expenses.length > 0 ? (
          <DataTable
            columns={[
              {
                key: 'checkbox',
                label: (
                  <input
                    type="checkbox"
                    checked={expenses.length > 0 && checkedExpenses.size === expenses.length}
                    onChange={(e) => toggleAllExpensesCheck(e.target.checked)}
                    className="rounded"
                  />
                ),
                sortable: false,
                render: (expense) => (
                  <input
                    type="checkbox"
                    checked={checkedExpenses.has(expense._id)}
                    onChange={() => toggleExpenseCheck(expense._id)}
                    className="rounded"
                  />
                ),
              },
              { 
                key: 'date', 
                label: 'Date', 
                render: (expense) => {
                  try {
                    const date = new Date(expense.date);
                    return isNaN(date.getTime()) ? 'N/A' : date.toISOString().split('T')[0];
                  } catch (e) {
                    return 'N/A';
                  }
                },
                exportRender: (expense) => {
                  try {
                    const date = new Date(expense.date);
                    return isNaN(date.getTime()) ? 'N/A' : date.toISOString().split('T')[0];
                  } catch (e) {
                    return 'N/A';
                  }
                }
              },
              { key: 'description', label: 'Description', render: (expense) => expense.description || 'N/A' },
              { 
                key: 'amount', 
                label: 'Amount', 
                render: (expense) => (
                  <span className="flex items-center gap-1 font-semibold text-primary-900 dark:text-primary-50">
                    <DirhamSymbol size={16} inline />
                    {((expense.amount || expense.amt || 0)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                ),
                exportRender: (expense) => (expense.amount || expense.amt || 0).toFixed(2)
              },
              ...(currentUser?.role === 'admin' ? [{
                key: 'actions',
                label: 'Actions',
                sortable: false,
                render: (expense) => (
                  <div className="flex items-center justify-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleOpenModal(expense)}
                      className="text-warning-600 dark:text-warning-400"
                      title="Edit"
                    >
                      <Edit size={18} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(expense._id)}
                      className="text-error-600 dark:text-error-400"
                      title="Delete"
                    >
                      <Trash2 size={18} />
                    </Button>
                  </div>
                ),
              }] : []),
            ]}
            data={expenses}
            striped
            hover
            exportFileName="expenses"
            searchableFields={['description', 'category']}
          />
        ) : (
          <div className="text-center py-8 text-primary-600 dark:text-primary-400">
            No expenses found
          </div>
        )}
      </Card>

      {/* Modal */}
      {showModal && (
        <Modal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          title={editingExpense ? 'Edit Expense' : 'Add New Expense'}
        >
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-primary-700 dark:text-primary-300 mb-2">
                Amount
              </label>
              <div className="relative">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  required
                  value={formData.amount || ''}
                  onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) || 0 })}
                  placeholder="Enter amount"
                  className="w-full px-4 py-2.5 rounded-lg border border-primary-200 dark:border-primary-700 bg-white dark:bg-primary-800 text-primary-900 dark:text-primary-50 placeholder-primary-400 dark:placeholder-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-transparent transition-all"
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                  <DirhamSymbol size={18} className="text-primary-500 dark:text-primary-400" />
                </div>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-primary-700 dark:text-primary-300 mb-2">
                Description
              </label>
              <textarea
                required
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe the expense purpose and details..."
                rows={4}
                className="w-full px-4 py-2.5 rounded-lg border border-primary-200 dark:border-primary-700 bg-white dark:bg-primary-800 text-primary-900 dark:text-primary-50 placeholder-primary-400 dark:placeholder-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-transparent transition-all resize-y"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-primary-700 dark:text-primary-300 mb-2">
                Date
              </label>
              <FormDatePicker
                name="date"
                required
                value={formData.date}
                onChange={(e) => {
                  if (e.target.value) {
                    const dateMatch = e.target.value.match(/^(\d{4})-(\d{2})-(\d{2})/);
                    if (dateMatch) {
                      const year = parseInt(dateMatch[1], 10);
                      const month = parseInt(dateMatch[2], 10) - 1;
                      const day = parseInt(dateMatch[3], 10);
                      const dateValue = new Date(year, month, day);
                      setFormData({ ...formData, date: dateValue });
                    } else {
                      setFormData({ ...formData, date: formData.date || new Date() });
                    }
                  } else {
                    setFormData({ ...formData, date: formData.date || new Date() });
                  }
                }}
                placeholderText="dd/mm/yyyy"
              />
            </div>
            <div className="flex gap-3 pt-4">
              <Button type="submit" variant="primary" className="flex-1">
                {editingExpense ? 'Update' : 'Create'}
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="flex-1">
                Cancel
              </Button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
};

export default Expenses;
